/**
 * The app's autosave engine, and the types that go through it.
 *
 * One instance for the process, because a slot is per ROW and two engines would
 * each think they had the only edit for a row they share. `createAutosaveEngine`
 * stays exported for the specs, which want a fresh one per case.
 *
 * Senders are registered here rather than passed per call so that the drain
 * added in commit 8 can re-send a row whose surface is long gone — there is no
 * closure left to ask by then.
 */
import { useToast } from '@team-hogc/hogc-one-design-system';
import * as transport from '@/utils/courses-annotations-transport';
import { createAutosaveEngine, keyFor } from './engine';
import { AUTOSAVE_TYPE } from './types';
import { outbox } from './outbox';
import { blankScopeOf, drainOutbox, isSealedEntry, overlayOutbox } from './drain';
import { pendingOverlay } from './pending-overlay';
import { enrolmentKnown } from '@/utils/courses-encryption/sealed-writes';
import { statusNeverRead } from '@/utils/courses-encryption/status-prime';
import { AUTOSAVE_EVENT, countAutosaveEvent } from './telemetry';

export { createAutosaveEngine, keyFor, AUTOSAVE_TYPE, outbox, blankScopeOf, pendingOverlay };

/**
 * ONE debounce policy for all four autosaving surfaces (write-volume plan, item
 * 1c). An earlier draft had two sets of numbers, one for notes and one for
 * blanks, and the difference was not observable.
 *
 * 2 s idle, because 400 ms — what the drawer used — is below a normal thinking
 * pause, so a typist pausing mid-sentence fired a save. 10 s ceiling from the
 * first keystroke of a burst, because the idle timer alone never fires for
 * someone who does not pause, and the ceiling is what stops them going unsaved
 * indefinitely.
 *
 * Neither number is what actually makes the data durable — the flush points do
 * that (plan §7): blur, the Done tick, a page turn, leaving the screen, and
 * `pause`. The timers are the fallback for a member who does none of those.
 */
export const AUTOSAVE_DEBOUNCE_MS = 2000;
export const AUTOSAVE_CEILING_MS = 10000;

/** A note's queue key. */
export const noteKey = noteId => keyFor(AUTOSAVE_TYPE.NOTE, noteId);

/**
 * A blank answer's queue key. Its item id is the composite the upsert is keyed
 * on — `programSlug/lessonSlug/blockId`, from the transport's
 * `blankAnswerItemId` — because a blank has no row id of its own.
 */
export const blankKey = itemId => keyFor(AUTOSAVE_TYPE.BLANK, itemId);

/**
 * The app instance. `isSignedInMember` and the status reporting are installed by
 * `installAutosave`, once there is a store to bind them to.
 */
export const autosave = createAutosaveEngine({
  debounceMs: AUTOSAVE_DEBOUNCE_MS,
  ceilingMs: AUTOSAVE_CEILING_MS,
  // The engine's event names are the telemetry's (spec §10), so this is a
  // pass-through; the mapping is one place to look if either side renames.
  onEvent: (event, detail) => countAutosaveEvent(event, detail),
});

/**
 * Point the engine at the signed-in member, so an edit made by one member on a
 * shared phone can never be sent under the next member's token.
 *
 * Read from `profile.id`, which is what `annotationAuth` sends as `memberId`
 * and therefore what an edit is really bound to. A null member (profile not
 * loaded yet) is permitted: it is the same "send and let the API judge" the
 * store has always done, and refusing here would silently drop the edits of a
 * member whose profile is merely slow.
 *
 * @param {{ store: object }} args
 * @returns {() => void} removes the failure listeners again — for specs, and for
 *   anything that installs a second time
 */
/** What the transport needs to bind an envelope to its owner. */
const authOf = store => ({
  token: store.state?.user?.token,
  // `profile` spreads the member's fields onto its own module state, so the id
  // is `profile.id` — the same read `courses.js`'s `annotationAuth` does.
  memberId: store.state?.profile?.id ?? null,
});

/**
 * Whether the member is enrolled, for a drain that is about to replay a body
 * queued in the clear — READ, not assumed.
 *
 * `enrolmentKnown` answers from what the device holds: a key, or a status read
 * this session. At launch a keyless device has neither — the launch read is for
 * key holders, and the pages that prime the status have not opened yet — so
 * "not known" used to become "not enrolled", and a plaintext body from before
 * the member enrolled on their other phone went over the wire in the clear, to
 * be refused. The sealing hook's own header forbids exactly that for a live
 * write, and the API logs request bodies. The drain is not inside the pause
 * window, so it can afford the one read the hook would have made.
 *
 * Only asked when there IS a plaintext entry to decide about, so a queue of
 * envelopes costs no request. `null` means "still not known": the read failed,
 * and the drain holds the plaintext entries for the next attempt rather than
 * guessing either way.
 *
 * @returns {Promise<boolean | null>}
 */
async function enrolmentForDrain(store, entries) {
  if (enrolmentKnown(store)) return true;
  if (!entries.some(entry => !isSealedEntry(entry))) return false;
  if (statusNeverRead(store)) {
    // `Promise.resolve`: a store without the module answers `undefined`.
    await Promise.resolve(store.dispatch('courses-devices/refreshStatus')).catch(() => {});
  }
  if (statusNeverRead(store)) return null;
  return enrolmentKnown(store);
}

/**
 * Send what an earlier session could not, and put it back on screen first.
 *
 * Called at launch once the member and their keys are hydrated, when this
 * device comes to hold the key, on every resume and when the network returns.
 * Safe to call when there is nothing to do — the usual case, since an entry
 * only survives a session that ended badly.
 *
 * ONE DRAIN AT A TIME, AND AT MOST ONE MORE AFTER IT. Two of those triggers fire
 * on the same tick at a cold launch for a key holder — the hydration gate
 * resolving and `hasMasterKey` turning true — and two drains reading the same
 * entries would send each of them twice: the second PATCH earns a 409 and
 * yields against the member's own first send. So a drain requested while one
 * is running is not started; it is remembered, and the running drain is
 * followed by exactly one more, because the request may have been the key
 * arriving mid-drain, which is what makes a plaintext entry sendable.
 *
 * @param {{ store: object, memberId: number|null, keyIds?: string[]|null }} args
 */
export function drainAutosaveOutbox(args) {
  // The newest caller's view of the world — its `keyIds` are the point of a
  // re-run: the drain that was running started before the key arrived.
  latestArgs = args;
  if (running) {
    rerunRequested = true;
    return running;
  }
  running = runDrain(args).finally(() => {
    running = null;
    if (rerunRequested) {
      rerunRequested = false;
      // Not awaited by the first caller: they asked for the drain that ran.
      drainAutosaveOutbox(latestArgs).catch(() => {});
    }
  });
  return running;
}

let running = null;
let rerunRequested = false;
let latestArgs = null;

/** Specs only: the engine is one instance for the process, and so is this gate. */
export function resetDrainGateForTests() {
  running = null;
  rerunRequested = false;
  latestArgs = null;
}

async function runDrain({ store, memberId, keyIds = null }) {
  if (memberId === null || memberId === undefined) return null;
  // Sign-out removes a member's queue, so only this member's should be here; a
  // sign-out that died before reaching that removal is what the first call
  // catches. Then this member's own entries that have waited too long.
  await outbox.sweepOthers(memberId);
  const aged = await outbox.sweepMember(memberId);
  if (aged) countAutosaveEvent(AUTOSAVE_EVENT.OUTBOX_DROPPED_AGE, { count: aged });
  const stored = await outbox.read(memberId);
  // A KEY IS OWNED BY EXACTLY ONE OF THE ENGINE OR THE DRAIN AT A TIME. The
  // outbox mirrors every live slot — `send` writes an entry before each attempt
  // and only removes it on success, `pause` writes one for every waiting slot —
  // so after a resume or an `online` event the engine and this drain would both
  // send the same body. For a note both carry the same `expectedRevision`, the
  // second earns a 409, and decision 9.2 turns that into a "Recovered"
  // duplicate the member never edited twice. The live slot wins: its own send
  // replaces and then removes the entry, and `retryNow` has already woken it.
  const entries = stored.filter(entry => !autosave.hasPending(entry.key));
  // The engine has the newer body for those; nothing on disk should speak for them.
  stored.filter(entry => autosave.hasPending(entry.key)).forEach(e => pendingOverlay.clear(e.key));
  if (!entries.length) return null;
  const auth = authOf(store);
  // Overlay BEFORE the drain, and before anything paints: see `drain.js`.
  await overlayOutbox({ entries, auth, store });
  // Whether a body queued in the clear has to be sealed before it can be sent.
  // The SAME question the sealing hook asks, through the same function, so the
  // two cannot answer differently for one write — see `enrolmentKnown`.
  const report = await drainOutbox({
    entries,
    auth,
    outbox,
    memberId,
    keyIds,
    store,
    enrolled: await enrolmentForDrain(store, entries),
    // Asked again per entry: the filter above is a snapshot, and the member
    // can start typing into a replayed row while the loop is still on another.
    isLive: key => autosave.hasPending(key),
  });

  // A dropped entry is the one place in this design where a member's writing is
  // lost, so it is told ONCE rather than counted silently. It happens when the
  // key it was sealed under became unrecoverable while it waited, inside this
  // signed-in session — a reset, or "Sign out all other devices", which is a
  // rotation whose commit cannot be undone, done from ANOTHER of the member's
  // devices while this one held the entry. Nothing can recover such an entry:
  // it can neither be sent (nobody could open the result) nor re-sealed (this
  // device no longer holds the key). Nothing survives a sign-out (plan 9.3), so
  // the copy no longer speaks of one.
  if (report?.dropped) {
    countAutosaveEvent(AUTOSAVE_EVENT.OUTBOX_DROPPED_DEAD_KEY, { count: report.dropped });
    // THE DESIGN SYSTEM'S TOAST, not the app's `toast` Vuex module. That module
    // is only seen by a page mounting `Toast.vue`, and no Home, Hub or lesson
    // page does — so the message the member most needs was dispatched into
    // nothing. An `ion-toast` presents itself into `ion-app`, so it appears
    // wherever the drain happens to fire. `bottomNav` keeps it clear of Home's
    // tab bar and falls back to a default clearance on pages without one. The
    // `.catch` stays because Ionic's controller can be gone in a teardown path.
    useToast()
      .error(
        report.dropped === 1
          ? "An edit couldn't be saved, because your encryption key was changed on another device."
          : `${report.dropped} edits couldn't be saved, because your encryption key was changed on another device.`,
        { duration: 4000, position: 'bottom', bottomNav: true },
      )
      .catch(() => {});
  }

  // The other place writing is lost, and the rarer one: an edit queued before
  // the member enrolled that could not be sealed afterwards — a note whose
  // revision this device never read, so the API would refuse the update
  // whatever we sent. Told on the same principle as the drop above, in its own
  // words, because "your encryption key was changed" is not what happened.
  // Counted, not toasted: the row is gone or the body was refused, and the
  // live engine says nothing for the same verdict on a slot (`dropped`).
  if (report?.refused) {
    countAutosaveEvent(AUTOSAVE_EVENT.OUTBOX_DROPPED_REFUSED, { count: report.refused });
  }

  if (report?.droppedPlaintext) {
    countAutosaveEvent(AUTOSAVE_EVENT.OUTBOX_DROPPED_PLAINTEXT, {
      count: report.droppedPlaintext,
    });
    useToast()
      .error(
        report.droppedPlaintext === 1
          ? "An edit you made before setting up encryption couldn't be saved. Please check that note."
          : `${report.droppedPlaintext} edits you made before setting up encryption couldn't be saved. Please check those notes.`,
        { duration: 4000, position: 'bottom', bottomNav: true },
      )
      .catch(() => {});
  }
  return report;
}

export function installAutosave({ store }) {
  if (!store) throw new TypeError('installAutosave needs the root Vuex store');

  autosave.setMemberCheck(member => {
    if (member === null || member === undefined) return true;
    const signedIn = authOf(store).memberId;
    return signedIn === null || signedIn === member;
  });

  // ONE sender per type, here rather than in a component. Two surfaces write
  // notes — the Notes Hub and the in-lesson drawer — and a slot is per ROW, so
  // a sender registered by each would have the second overwrite the first and
  // leave whichever screen mounted last deciding how everyone's edits are sent.
  // Both types go through the sealed path from commit 9: the engine seals so the
  // envelope can be persisted before the request, and the sender puts that same
  // envelope on the wire rather than sealing a second time. A note's envelope
  // carries its `expectedRevision`, which is why persisting it verbatim is also
  // how `baseRevision` survives a restart — there is nothing extra to store.
  autosave.registerSender(AUTOSAVE_TYPE.NOTE, (noteId, sealed) =>
    store.dispatch('courses/writeNoteBody', { noteId, sealed }),
  );
  // Blanks go through the sealed path from phase 3: the engine seals so the
  // envelope can be persisted before the request, and the sender puts that same
  // envelope on the wire rather than sealing a second time.
  // The sentinel is passed through RATHER THAN FLATTENED TO `null`, which is
  // what this used to do: it carries the revision the clear is held to now, and
  // `null` would throw that away and delete the row unconditionally.
  // `sendSealed` recognises the sentinel, the object Preferences parsed back,
  // and `null` alike.
  autosave.registerSender(AUTOSAVE_TYPE.BLANK, (itemId, sealed) =>
    transport.sendSealed(authOf(store), {
      type: AUTOSAVE_TYPE.BLANK,
      itemId,
      sealed,
      scope: blankScopeOf(itemId),
    }),
  );

  // Seal-then-persist, for both types: the envelope goes to disk before the
  // request, and a note's `expectedRevision` rides inside it (§4).
  autosave.setDurableStore(
    { put: (memberId, entry) => outbox.put(memberId, entry), remove: outbox.remove },
    (type, itemId, body, options) =>
      transport.sealAnnotation(authOf(store), type, itemId, body, options),
  );

  // What a failure MEANS is the store's to decide, for every surface at once.
  // A screen that also wants to say something about it adds its own listener
  // and removes it on unmount.
  const report = failure => {
    store.commit('courses-autosave/setFailed', { key: failure.key, error: failure.error });
    return store.dispatch('courses/onAutosaveFailure', failure);
  };
  const stopNote = autosave.onSendError(AUTOSAVE_TYPE.NOTE, report);
  const stopBlank = autosave.onSendError(AUTOSAVE_TYPE.BLANK, report);

  // What the member sees while a write is in hand (plan §6.5). The engine knows
  // which rows are unsaved; the store is where a component can read it.
  autosave.setPendingReporter(keys => {
    store.commit('courses-autosave/setPending', keys);
    store.commit('courses-autosave/setParked', autosave.parkedKeys());
  });

  // A row that reached the server has nothing left to say. Only a success
  // clears a failure: a slot leaves the pending set when its send SETTLES,
  // which includes settling by failing.
  // Landing also ends any body the outbox was holding for the row: the engine
  // took the key over when the member typed, and the server now has the newer
  // one, so a later fetch must be believed rather than overlaid.
  autosave.onPersisted(AUTOSAVE_TYPE.NOTE, itemId => {
    pendingOverlay.clear(noteKey(itemId));
    store.commit('courses-autosave/clearFailed', noteKey(itemId));
  });
  autosave.onPersisted(AUTOSAVE_TYPE.BLANK, itemId => {
    pendingOverlay.clear(blankKey(itemId));
    store.commit('courses-autosave/clearFailed', blankKey(itemId));
  });

  return () => {
    stopNote();
    stopBlank();
    autosave.setPendingReporter(null);
  };
}
